import { MouseEvent, useCallback, useMemo, useState } from 'react';
import cn from 'classnames';

import { convertQuillDeltaToHtml } from 'functions/convertQuillDeltaToHtml';
import { IReactQuillDelta } from 'APIPackage/KnmModule/interfaces/IReactQuillDelta';
import { ViolationStatus } from 'KnmErknmPackage/ErknmViolationsModule/components/ViolationStatus';
import { IKnmErknmFormViolation } from 'KnmErknmPackage/ErknmViolationsModule/interfaces/IKnmErknmFormViolation';
import { IconCollapse } from 'UIPackage/IconsModule/components/IconCollapse';
import { IKnmErknmFormRequirementNpa } from 'KnmErknmPackage/MainModule/interfaces/IKnmErknmFormRequirementNpa';
import { IKnmErknmFormViolatedRequirementsMandatory } from 'KnmErknmPackage/MainModule/interfaces/IKnmErknmFormViolatedRequirementsNpa';
import { CheckboxField } from 'UIPackage/MainModule/components/CheckboxField';
import { Button } from 'UIPackage/MainModule/components/Button';
import { ViolationStatusesEnum } from 'APIPackage/KnmModule/enums/ViolationStatusesEnum';
import { InspectionResultActionTypeEnum } from 'APIPackage/CatalogsModule/enums/InspectionResultActionTypeEnum';
import { InspectionResultFieldEnum } from 'APIPackage/CatalogsModule/enums/InspectionResultFieldEnum';
import { ErknmInspectionResultMessage } from 'KnmErknmPackage/MainModule/components/ErknmInspectionResultMessageMessage';

import styles from './ViolationItem.module.scss';

interface IViolationItemProps {
  violation: IKnmErknmFormViolation;
  requirementNpa: IKnmErknmFormRequirementNpa;
  mandatory: IKnmErknmFormViolatedRequirementsMandatory;
  violationName: string;
  checkboxDisabled: boolean;
  targetErpId?: string;
}

export const ViolationItem = ({
  violation,
  requirementNpa,
  mandatory,
  violationName,
  checkboxDisabled,
  targetErpId,
}: IViolationItemProps) => {
  const [isOpen, setIsOpen] = useState(false);

  /**
   * Функция изменения состояния компонента Открыт/Закрыт
   */
  const onChangeOpen = useCallback((e: MouseEvent<HTMLElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsOpen(val => !val);
  }, []);

  /**
   * Обработчик нажатия кнопки "Оценить представленные материалы"
   */
  const onClickOpenPrescription = useCallback(() => {
    if (violation.prescriptionLink) {
      const url = new URL(violation.prescriptionLink, window.location.origin);

      url.searchParams.set('expandAllInPrescription', 'true');
      window.open(url.toString(), '_blank');
    }
  }, [violation]);

  /**
   * Вычитывание ошибки при проверках за 5 денй
   */
  const inspectionResultError = useMemo(() => {
    const inspectionRed = requirementNpa.inspectionResults?.find(
      i =>
        i.resultActionType === InspectionResultActionTypeEnum.LIGHT_RED &&
        (i.inspectedField === InspectionResultFieldEnum.ROT_REGISTRY_REQUIREMENTS_NPA ||
          i.inspectedField === InspectionResultFieldEnum.ROT_REGISTRY_STRUCTURAL_UNIT),
    );
    const inspectionYellow = requirementNpa.inspectionResults?.find(
      i =>
        i.resultActionType === InspectionResultActionTypeEnum.LIGHT_YELLOW &&
        (i.inspectedField === InspectionResultFieldEnum.ROT_REGISTRY_REQUIREMENTS_NPA ||
          i.inspectedField === InspectionResultFieldEnum.ROT_REGISTRY_STRUCTURAL_UNIT),
    );
    let inspectionMessage = '';

    if (requirementNpa.inspectionResults) {
      inspectionMessage = requirementNpa.inspectionResults
        ?.filter(
          i =>
            i.inspectedField === InspectionResultFieldEnum.ROT_REGISTRY_REQUIREMENTS_NPA ||
            i.inspectedField === InspectionResultFieldEnum.ROT_REGISTRY_STRUCTURAL_UNIT,
        )
        .map(i => i.inspectionMessage)
        .join(', ');
    }

    return {
      inspectionRed,
      inspectionYellow,
      inspectionMessage,
    };
  }, [requirementNpa.inspectionResults]);

  return (
    <div
      key={violation.id}
      className={cn(styles.ViolationItemWrapper, {
        [styles.Transparent]: !violation.includeInSubject,
        [styles.RequirementsNpaItemBlockInputInspectionResultYellow]: !!inspectionResultError.inspectionYellow,
        [styles.RequirementsNpaItemBlockInputInspectionResultRed]: !!inspectionResultError.inspectionRed,
      })}
    >
      <div className={cn(styles.Row, styles.Header)}>
        {(inspectionResultError.inspectionRed || inspectionResultError.inspectionYellow) && (
          <ErknmInspectionResultMessage
            inspectionResult={{
              inspectedField: InspectionResultFieldEnum.ROT_REGISTRY_REQUIREMENTS_NPA,
              inspectionMessage: inspectionResultError.inspectionMessage,
              resultActionType: inspectionResultError.inspectionRed
                ? InspectionResultActionTypeEnum.LIGHT_RED
                : InspectionResultActionTypeEnum.LIGHT_YELLOW,
            }}
          />
        )}
        <div className={styles.InnerHeaderWrapper}>
          <div className={cn(styles.Cell, styles.CellWidthDescription)}>
            <div className={styles.DescriptionWrapper}>
              <span>Нарушение №{violation.number}</span>
              <div
                /* eslint-disable-next-line react/no-danger */
                dangerouslySetInnerHTML={{
                  __html: convertQuillDeltaToHtml(violation.descriptionReactQuillEditor as IReactQuillDelta),
                }}
              />
              <div className={styles.IncludeInSubjectCheckboxWrapper}>
                <CheckboxField
                  id={`includeInSubject-${violation.id}`}
                  name={`${violationName}.includeInSubject`}
                  label="Включить в предмет проверки"
                  theme="system"
                  type="checkbox"
                  disabled={checkboxDisabled}
                />
              </div>
            </div>
          </div>
          <div className={cn(styles.Cell, styles.CellWidthStatus)}>
            <div className={styles.StatusWrapper}>
              <div className={styles.StatusInnerWrapper}>
                <ViolationStatus status={violation.status} />
              </div>
              {violation.status === ViolationStatusesEnum.APPROVING && (
                <Button
                  color="primary"
                  text={'Оценить представленные материалы'}
                  type={'button'}
                  onClick={onClickOpenPrescription}
                />
              )}
            </div>
            <div className={cn(styles.Arrow, { [styles.ArrowRotate]: isOpen })} onClick={onChangeOpen}>
              <IconCollapse className={styles.OpenButtonIcon} />
            </div>
          </div>
        </div>
      </div>
      {isOpen && (
        <div className={styles.InnerWrapper}>
          <div className={styles.BoldNpaText}>{requirementNpa.npa.title}</div>
          <span className={styles.GrayNpaText}>
            Структурная единица <span className={styles.BoldNpaText}>{requirementNpa.structuralUnit.title}</span>
          </span>

          <div className={styles.RequireViolationWrapper}>
            <div className={styles.GrayNpaText}>Нарушенные обязательные требования</div>

            <div className={cn(styles.RequirementsItemBlock)}>
              <div className={styles.NpaBlock}>
                <div className={styles.RequirementsHeader}>
                  <span className={styles.GreyHeader}>Содержание требования</span>
                </div>
                {mandatory.mandatory.title}
              </div>
              <div className={styles.StructuralUnitBlock}>
                <div className={styles.RequirementsHeader}>
                  <span className={styles.GreyHeader}>Значимость последствий нарушения</span>
                </div>
                <div className={styles.RequirementsValueWrapper}>{mandatory.category?.label}</div>
              </div>
            </div>
            {violation.status === ViolationStatusesEnum.APPROVING &&
              violation.prescriptionLink &&
              (violation.targetErpId || targetErpId) && (
                <div className={styles.GrayNpaText}>
                  Ссылка на паспорт исходной проверки{' '}
                  <a
                    className={styles.LinkTargetPassport}
                    href={violation.prescriptionLink}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {violation.targetErpId || targetErpId}
                  </a>
                </div>
              )}
          </div>
        </div>
      )}
    </div>
  );
};
